if(!customElements.get('change-plan-310')) {

    // ------------------------------------------------------------------------------
    // MODULE-LEVEL state — shared across EVERY change-plan-310 instance AND every cart
    // re-render. The cart drawer rebuilds by replacing innerHTML (cart-drawer.js
    // renderContents / CartItems.onCartUpdate), which DESTROYS and RECREATES these
    // custom elements, re-firing connectedCallback. Per-instance flags (this.tierBusy)
    // and the old window-level per-line Set were defeated by that reconnect + by the
    // line's control being rendered in more than one place: two elements ran swapTier
    // for the same line, both cleared the guard, and TWO /cart/add (2-Bag qty1) raced
    // into two lines ($287.96) instead of ONE ($143.98). These live at module scope so
    // they survive the add -> remove -> refreshCart reconnect cycle.
    // ------------------------------------------------------------------------------

    // Tier swaps currently IN FLIGHT. Keyed BOTH by source line ('src:<lineKey>') and by
    // target add ('dst:<variantId>:<planId>'). A swap acquires both keys SYNCHRONOUSLY
    // (before its first await / /cart/add). Any concurrent element instance, re-render,
    // or rapid click that would swap the same source line — or add the same target tier
    // — no-ops. This is the money-path guard: two adds toward the 2-Bag variant can
    // never both be in flight (dst lock), and one source line can never be swapped twice
    // at once (src lock). Released only after the add->remove->refresh cycle finishes.
    const TIER_SWAP_INFLIGHT = new Set();

    // Line keys whose LEGACY stacked state (Shopify line qty > 1) has already been
    // auto-normalized this page load. A given stacked line normalizes AT MOST ONCE, so a
    // re-render that reconnects a fresh element for a still-present stale line cannot
    // re-add. (Permanent for the page: the normalized source line is removed on success;
    // on a partial failure we deliberately do NOT retry, to avoid re-adding.)
    const TIER_NORMALIZED = new Set();

    // The single change-plan-310 element that currently OWNS a given line key. The theme
    // can render the same line's control more than once, and re-renders overlap old+new
    // elements. Only the owner wires the stepper + normalizes; duplicate elements no-op.
    const TIER_LINE_OWNERS = new Map();

    customElements.define('change-plan-310', class ChangePlan310 extends HTMLElement {
        constructor() {
            super();
        }

        connectedCallback() {
            this.init();
        }

        disconnectedCallback() {
            // Release line ownership so the next render's element can claim + wire it.
            if (this.tierLineKey && TIER_LINE_OWNERS.get(this.tierLineKey) === this) {
                TIER_LINE_OWNERS.delete(this.tierLineKey);
            }
        }

        init() {
            this.lineIndex = this.dataset.lineIndex;
            this.lineQuantity = this.dataset.lineQuantity;
            this.cartItem = this.closest('.cart-item');
            this.loadingElement = this.cartItem?.querySelector('.loading-overlay');

            this.select = this.querySelector('select');
            this.button = this.querySelector('button');

            this.handlePlanChange = this.handlePlanChange.bind(this);
            this.handlePlanClick = this.handlePlanClick.bind(this);
            this.setLoading = this.setLoading.bind(this);

            this.select?.addEventListener('change', (e) => {
                this.handlePlanChange(e);
            });

            this.button?.addEventListener('click', (e) => {
                this.handlePlanClick(e);
            });

            // Usage-tier bag stepper: on a subscription tier line the +/- quantity
            // control becomes a bag-tier stepper (promotes/demotes the tier variant)
            // instead of multiplying the current (lower-tier) variant. Also normalizes
            // any legacy stacked state (qty > 1) that predates this fix.
            this.wireTierStepper();
        }

        wireTierStepper() {
            this.tierInput = this.cartItem && this.cartItem.querySelector('input[data-type="tier-stepper"]');
            if (!this.tierInput) return;

            this.tierLineKey = this.tierInput.dataset.lineKey || this.dataset.lineKey;

            // Dedupe duplicate elements for the SAME cart line: only ONE instance wires +
            // normalizes. If a still-connected element already owns this line key, no-op
            // (prevents two elements both normalizing / both stepping the same line).
            const owner = TIER_LINE_OWNERS.get(this.tierLineKey);
            if (owner && owner !== this && owner.isConnected) return;
            TIER_LINE_OWNERS.set(this.tierLineKey, this);

            try {
                this.tierLadder = (JSON.parse(this.tierInput.dataset.tierLadder || '[]') || [])
                    .sort((a, b) => a.bags - b.bags);
            } catch (e) {
                this.tierLadder = [];
            }
            if (this.tierLadder.length < 2) return; // nothing to step between

            this.currentBags = parseInt(this.tierInput.dataset.currentBags, 10);

            this.tierInput.addEventListener('change', (e) => {
                e.preventDefault();
                this.handleTierStep(parseInt(e.target.value, 10));
            });

            // Legacy normalize: a tier line that loaded with qty > 1 is the stacked bug
            // (e.g. 1-Bag x2 @16%). Snap it to the correct single tier variant @ qty 1.
            const lineQty = parseInt(this.lineQuantity, 10) || 1;
            if (lineQty > 1) this.normalizeTier(lineQty);
        }

        // Directional single-tier step. + moves to the next larger tier, - to the next
        // smaller tier (data-driven — the 14-srv ladder skips 3 bags). Cap/floor at the
        // ends restore the displayed bag count without touching the cart.
        handleTierStep(newVal) {
            if (this.tierBusy) return;
            if (isNaN(newVal)) { this.tierInput.value = this.currentBags; return; }

            let target = null;
            if (newVal > this.currentBags) {
                target = this.tierLadder.find((t) => t.bags > this.currentBags);
            } else if (newVal < this.currentBags) {
                const lower = this.tierLadder.filter((t) => t.bags < this.currentBags);
                target = lower.length ? lower[lower.length - 1] : null;
            }

            if (!target || target.bags === this.currentBags) {
                this.tierInput.value = this.currentBags; // cap / floor / no-op
                return;
            }
            this.tierBusy = true;
            this.swapTier(target.variantId, target.planId);
        }

        // Legacy stacked-state normalizer. desiredBags = currentBags * lineQty, snapped
        // DOWN to the largest available tier (cap = top tier). Guarded per line key by
        // TIER_NORMALIZED so a partial (add-ok / remove-failed) cart or a re-render can't
        // loop or double-add. The concurrency guard in swapTier is the real backstop.
        normalizeTier(lineQty) {
            if (TIER_NORMALIZED.has(this.tierLineKey)) return;
            TIER_NORMALIZED.add(this.tierLineKey);

            const desiredBags = this.currentBags * lineQty;
            const atOrBelow = this.tierLadder.filter((t) => t.bags <= desiredBags);
            const target = atOrBelow.length ? atOrBelow[atOrBelow.length - 1] : this.tierLadder[0];
            if (target.bags !== this.currentBags) {
                this.tierBusy = true;
                this.swapTier(target.variantId, target.planId);
            }
        }

        handlePlanChange(e) {
            e.preventDefault();
            const selectedPlan = e.target.value;
            this.changePlan(selectedPlan);
        }

        async handlePlanClick(e) {
            e.preventDefault();
            const selectedPlan = this.button.dataset.value;
            this.changePlan(selectedPlan);
        }

        changePlan(selectedPlan) {
            // usage-tier swap: "<variant_id>:<plan_id>" moves the line to a
            // different variant (another bag tier) with that variant's plan
            if (selectedPlan.includes(':')) {
                const [variantId, planId] = selectedPlan.split(':');
                this.swapTier(variantId, planId);
                return;
            }
            const formData = {
                line: parseInt(this.lineIndex),
                selling_plan: parseInt(selectedPlan),
                quantity: parseInt(this.lineQuantity)
            }
            this.changeSellingPlan(formData);
        }

        async swapTier(variantId, planId) {
            const srcKey = 'src:' + (this.dataset.lineKey || this.tierLineKey || '');
            const dstKey = 'dst:' + variantId + ':' + (planId || '');

            // Concurrency-safe, idempotent LOCK. Acquired SYNCHRONOUSLY here, before any
            // await, so no second element instance, no re-render-reconnected element, and
            // no rapid double-click can start a parallel /cart/add for the same source
            // line OR the same target tier while one swap is in flight. This is the fix
            // for the double-add (two 2-Bag lines). If either key is already held, this
            // call no-ops and restores the displayed bag count.
            if (TIER_SWAP_INFLIGHT.has(srcKey) || TIER_SWAP_INFLIGHT.has(dstKey)) {
                this.tierBusy = false;
                if (this.tierInput && this.currentBags != null) this.tierInput.value = this.currentBags;
                return;
            }
            TIER_SWAP_INFLIGHT.add(srcKey);
            TIER_SWAP_INFLIGHT.add(dstKey);

            this.blockCheckoutButton();
            this.setLoading(true);
            try {
                // Tier swaps ALWAYS add quantity 1. A tier variant already encodes its bag
                // count (2 Bags = 1 line of the 2-Bag variant), so multiplying by the old
                // line quantity double-stacked bags (picking "2 Bags" on a qty-2 line made
                // 4 bags). The resulting line's bag count is carried by the variant itself.
                const addBody = { id: parseInt(variantId), quantity: 1 };
                if (planId) addBody.selling_plan = parseInt(planId);

                let response = await fetch(`/cart/add.js`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ items: [addBody] })
                });
                if (!response.ok) {
                    console.error('Tier swap: add failed', await response.json());
                    return;
                }

                response = await fetch(`/cart/change.js`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ id: this.dataset.lineKey, quantity: 0 })
                });
                if (!response.ok) {
                    console.error('Tier swap: remove failed', await response.json());
                    return;
                }

                publish(PUB_SUB_EVENTS.cartUpdate, {
                    source: "product-form",
                    cartData: response
                });
            } catch (error) {
                console.error('Network error / fetch:', error);
            } finally {
                this.setLoading(false);
                this.refreshCart();
                // Release AFTER the cycle. refreshCart() only KICKS OFF the async re-render;
                // by the time it resolves and reconnects elements, the source line is gone
                // (removed) so nothing re-normalizes, and TIER_NORMALIZED still blocks the
                // source key permanently as a second line of defense.
                TIER_SWAP_INFLIGHT.delete(srcKey);
                TIER_SWAP_INFLIGHT.delete(dstKey);
            }
        }

        setLoading(isLoading) {
            if (this.select) this.select.disabled = isLoading;
            if (this.tierInput) this.tierInput.disabled = isLoading;
            if (this.button) {
                this.button.setAttribute('disabled', isLoading);
                this.button.setAttribute('aria-disabled', isLoading);
            }
            if(this.loadingElement) {
                this.loadingElement.classList.remove('hidden');
            }
        }

        blockCheckoutButton() {
            const checkoutButton = document.querySelector(' cart-drawer button[name="checkout"]');
            if(checkoutButton) {
                checkoutButton.setAttribute('disabled', true);
            }
        }

        async changeSellingPlan(formData) {
            this.blockCheckoutButton();
            this.setLoading(true);
            try {
                const response = await fetch(`/cart/change.js`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify(formData)
                });

                if (!response.ok) {
                    const errorData = await response.json();
                    console.error('Błąd zmiany planu sprzedażowego (Shopify response not OK):', errorData);
                    return;
                }

                publish(PUB_SUB_EVENTS.cartUpdate, {
                    source: "product-form",
                    cartData: response
                  });

            } catch (error) {
                console.error('Network error / fetch:', error);
            } finally {
                this.setLoading(false);
                this.refreshCart();
            }
        }

        refreshCart() {
            const cartDrawer = document.querySelector('cart-drawer');
            if (cartDrawer && cartDrawer.refreshCartDrawer) {
                 cartDrawer.refreshCartDrawer();
            }
        }
    });
}
